import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';
import { toast } from 'sonner';

const CartContext = createContext();

const CART_STORAGE_KEY = 'lart_cart';

// Helper to safely read from localStorage
const readCartFromStorage = () => {
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    logger.error('Error reading cart from localStorage:', e);
  }
  return [];
};

// Helper to safely write to localStorage
const writeCartToStorage = (cart) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (e) {
    logger.error('Error writing cart to localStorage:', e);
  }
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize state directly from localStorage
  const [cart, setCart] = useState(() => readCartFromStorage());
  const [isCartOpen, setIsCartOpen] = useState(false);
  const isInitialMount = useRef(true);
  
  // Sync cart to localStorage whenever it changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    writeCartToStorage(cart);
  }, [cart]);
  
  // Also listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === CART_STORAGE_KEY) {
        const newCart = readCartFromStorage();
        setCart(newCart);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  const addToCart = useCallback((product) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find(item => item.id === product.id);
      
      let newCart;
      if (existingItem) {
        toast.success(`${product.name} toegevoegd (${existingItem.quantity + 1}x)`);
        newCart = prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        toast.success(`${product.name} toegevoegd aan winkelmandje`);
        newCart = [...prevCart, { ...product, quantity: 1 }];
      }
      
      // Write to storage immediately for reliability
      writeCartToStorage(newCart);
      return newCart;
    });
    
    setIsCartOpen(true);
  }, []);
  
  const removeFromCart = useCallback((productId) => {
    setCart((prevCart) => {
      const newCart = prevCart.filter(item => item.id !== productId);
      writeCartToStorage(newCart);
      return newCart;
    });
    toast.success('Verwijderd uit winkelmandje');
  }, []);
  
  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart((prevCart) => {
      const newCart = prevCart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      );
      writeCartToStorage(newCart);
      return newCart;
    });
  }, [removeFromCart]);
  
  const clearCart = useCallback(() => {
    setCart([]);
    writeCartToStorage([]);
    toast.success('Winkelmandje geleegd');
  }, []);
  
  const getCartTotal = useCallback(() => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  }, [cart]);
  
  const getCartCount = useCallback(() => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  }, [cart]);
  
  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        isCartOpen,
        setIsCartOpen
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
